import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { api } from '../../utils/api';
import { 
  TrendingUp, 
  Newspaper, 
  AppWindow, 
  CreditCard,
  Zap,
  Shield,
  Clock,
  ArrowUpRight,
  Package
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export const AdminDashboard = () => {
  const [stats, setStats] = useState({
    news: 0,
    apps: 0,
    plans: 0,
    events: 0
  });
  const [recentNews, setRecentNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [newsRes, appsRes, plansRes, statsRes] = await Promise.all([
          api.news.getAll(false),
          api.apps.getAll(),
          api.plans.getAll(),
          api.analytics.getStats(7)
        ]);
        
        setStats({
          news: newsRes.data?.length || 0,
          apps: appsRes.data?.length || 0,
          plans: plansRes.data?.length || 0,
          events: statsRes.data?.length || 0
        });
        setRecentNews(newsRes.data?.slice(0, 5) || []);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-12 h-12 border-2 border-[#C6FF00]/20 border-t-[#C6FF00] rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    { label: 'Noticias', value: stats.news, icon: Newspaper, color: '#C6FF00', change: '+12%' },
    { label: 'Aplicaciones', value: stats.apps, icon: AppWindow, color: '#C6FF00', change: '+5%' },
    { label: 'Planes Activos', value: stats.plans, icon: CreditCard, color: '#C6FF00', change: '0%' },
    { label: 'Eventos 7d', value: stats.events, icon: TrendingUp, color: '#C6FF00', change: '+24%' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-12">
        <header>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-[#C6FF00] font-black uppercase tracking-[0.3em] text-[10px] block mb-2">Central de Control</span>
            <h1 className="text-4xl font-black tracking-tighter uppercase italic">NÚCLEO OPERATIVO</h1>
          </motion.div>
        </header>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="p-8 rounded-[2rem] bg-white/[0.02] border border-white/5 hover:border-[#C6FF00]/20 transition-all group"
            >
              <div className="flex items-start justify-between mb-8">
                <div className="w-12 h-12 rounded-xl bg-[#C6FF00]/10 border border-[#C6FF00]/20 flex items-center justify-center text-[#C6FF00]">
                  <stat.icon size={20} />
                </div>
                <div className="flex items-center gap-1 text-[#C6FF00] text-xs font-black italic">
                  <span>{stat.change}</span>
                  <ArrowUpRight size={12} />
                </div>
              </div>
              <div className="text-3xl font-black text-white mb-2 tracking-tighter">{stat.value}</div>
              <div className="text-white/40 text-[10px] font-black uppercase tracking-widest">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Middle Section: Chart & Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 p-10 rounded-[3rem] bg-[#030303] border border-white/5 relative overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-12">
                <h3 className="text-xl font-black uppercase tracking-tight italic">Flujo de Actividad</h3>
              </div>
              
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={[
                    { name: 'Lun', val: 400 },
                    { name: 'Mar', val: 300 },
                    { name: 'Mie', val: 600 },
                    { name: 'Jue', val: 800 },
                    { name: 'Vie', val: 500 },
                    { name: 'Sab', val: 900 },
                    { name: 'Dom', val: 1200 },
                  ]}>
                    <defs>
                      <linearGradient id="colorVal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#C6FF00" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#C6FF00" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#000', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '1rem' }}
                      itemStyle={{ color: '#C6FF00', fontWeight: 'bold' }}
                    />
                    <Area type="monotone" dataKey="val" stroke="#C6FF00" strokeWidth={3} fillOpacity={1} fill="url(#colorVal)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 p-10 rounded-[3rem] bg-[#030303] border border-white/5 relative overflow-hidden">
            <div className="relative z-10 h-full flex flex-col">
              <h3 className="text-xl font-black uppercase tracking-tight italic mb-8">Últimas Señales</h3>
              <div className="space-y-6 flex-grow">
                {recentNews.length > 0 ? recentNews.map((item, i) => (
                  <div key={i} className="flex items-center gap-4 group cursor-pointer">
                    <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-[#C6FF00]/40 transition-colors">
                      <Newspaper size={16} className="text-white/40 group-hover:text-[#C6FF00]" />
                    </div>
                    <div className="overflow-hidden">
                      <div className="text-white text-xs font-black truncate uppercase group-hover:text-[#C6FF00] transition-colors">{item.title}</div>
                      <div className="text-white/30 text-[9px] font-black uppercase tracking-widest mt-1">
                        {new Date(item.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-white/20 text-[10px] font-black uppercase tracking-widest text-center py-12">Sin señales recientes</div>
                )}
              </div>
              <Link to="/admin/news" className="w-full py-4 bg-white/5 border border-white/5 rounded-2xl text-[10px] font-black uppercase tracking-widest text-center text-white/40 hover:bg-[#C6FF00] hover:text-black transition-all mt-8">
                Gestionar Noticias
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Section: System Status */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { label: 'Encriptación', desc: 'Protocolo de Nivel 9 Activo', icon: Shield, status: 'Operativo' },
            { label: 'Latencia', desc: '14ms desde el Núcleo', icon: Zap, status: 'Óptimo' },
            { label: 'Paquetes', desc: 'Tráfico de Datos Entrante', icon: Package, status: 'Fluido' },
          ].map((item, i) => (
            <div key={i} className="p-8 rounded-[2.5rem] bg-white/[0.02] border border-white/5 flex items-center gap-6">
              <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/20 shrink-0">
                <item.icon size={22} />
              </div>
              <div>
                <div className="text-white font-black text-xs uppercase tracking-tight mb-1">{item.label}</div>
                <div className="text-white/30 text-[10px] font-black uppercase tracking-widest mb-3">{item.desc}</div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C6FF00]"></div>
                  <span className="text-[10px] font-black uppercase text-[#C6FF00] tracking-widest">{item.status}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};
